import { useMemo } from "react";

import { type Column } from "@tanstack/react-table";

import { DatatableRowData, TSectionData } from "../datatable-types";
import { useDatatableContext } from "../use-datatable-context";

import { partitionToggleableColumnList } from "./partition-toggleable-column-list";
import { useColumnDisplayValue } from "./use-column-display-value";

interface ColumnToggleSectionList<TData extends DatatableRowData> {
  standardColumnList: Column<TSectionData<TData>>[];
  labelColumnList: Column<TSectionData<TData>>[];
}

export const useDatatableColumnToggleSectionList = <
  TData extends DatatableRowData,
>(): ColumnToggleSectionList<TData> => {
  const { table, lock } = useDatatableContext<TData>();
  const getDisplayValueForColumn = useColumnDisplayValue<TData>();
  const { canToggleColumnVisibility } = lock;

  return useMemo<ColumnToggleSectionList<TData>>(() => {
    if (!canToggleColumnVisibility) {
      return { standardColumnList: [], labelColumnList: [] };
    }

    return partitionToggleableColumnList({
      columnList: table.getAllColumns(),
      getDisplayValueForColumn,
    });
  }, [canToggleColumnVisibility, table, getDisplayValueForColumn]);
};
